import { DonutPlot } from './DonutPlot.js'
import { BarPlot } from './BarPlot.js'
import { PiePlot } from './PiePlot.js'
import { HistogramPlot } from './HistogramPlot.js'
import { LinePlot } from './LinePlot.js'
import { ParallelCoordinatePlot } from './ParallelCoordinatePlot.js'
import { ScatterPlot } from './ScatterPlot.js'

let _defaultParams = {
    digits:2,
    backgroundColor: "white",
    axisColor: '#555555',
    axisAngle: 0,
    gridColor: '#555555',
    gridOpacity: 0.2,
    labelColor: "white",
    labelSize: 10,
    labelAligned:true,
    defaultColor: "#555555",
    defaultOpacity: 0.75,
    defaultWidth: 1.5,
    defaultRadius: 3.5,
    selectionColor: '#0000ff',
    selectionOpacity: 0.5,
    selectionWidth: 3.0,
    highlightColor: "ff0a94",
    highlightOpacity: 0.5,
    highlightWidth: 3.0,
    margin : { top: 20, right: 20, bottom: 40, left: 40 },
};

class PlotMgr {
    #plotMap = {};

    constructor(initParams) {
        let params = { ..._defaultParams, ...(initParams || {}) };

        this.parameters = params;

        this.digits = params.digits;
        this.margin = params.margin;
        this.backgroundColor = params.backgroundColor;

        this.axisColor = params.axisColor;
        this.axisAngle = params.axisAngle;

        this.gridColor = params.gridColor;
        this.gridOpacity = params.gridOpacity;

        this.labelColor = params.labelColor;
        this.labelSize = params.labelSize;
        this.labelAligned = params.labelAligned;

        this.defaultColor = params.defaultColor;
        this.defaultOpacity = params.defaultOpacity;
        this.defaultWidth = params.defaultWidth;
        this.defaultRadius = params.defaultRadius;

        this.selectionColor = params.selectionColor;
        this.selectionOpacity = params.selectionOpacity;
        this.selectionWidth = params.selectionWidth;

        this.highlightColor = params.highlightColor;
        this.highlightOpacity = params.highlightOpacity;
        this.highlightWidth = params.highlightWidth;

        this.#plotMap["donut"] = [];
        this.#plotMap["pie"] = [];
        this.#plotMap["bar"] = [];
        this.#plotMap["histogram"] = [];
        this.#plotMap["line"] = [];
        this.#plotMap["ParallelCoordinate"] = [];
        this.#plotMap["scatter"] = [];
    }


    // select elements from all plots
    selectElementsInAllPlots(elementIDs) {
        for (var key in this.#plotMap) {
            if (this.#plotMap.hasOwnProperty(key)) {
                this.#plotMap[key].forEach(function (viewer) {
                    viewer.clearSelection();
                    viewer.selectElements(elementIDs);
                })
            }
        }
    }

    // highlight elements from all plots
    highlightElementsInAllPlots(elementIDs) {
        for (let key in this.#plotMap) {
            if (this.#plotMap.hasOwnProperty(key)) {
                this.#plotMap[key].forEach(function (viewer) {
                    viewer.clearHighlights();
                    viewer.highlightElements(elementIDs);
                })
            }
        }
    }

    // de select elements from all plots
    deSelectElementsInAllPlots() {
        for (var key in this.#plotMap) {
            if (this.#plotMap.hasOwnProperty(key)) {
                this.#plotMap[key].forEach(viewer => {
                    viewer.clearAllPickSelection();
                })
            }
        }
    }

    // de highlight elements from all plots
    deHighlightElementsInAllPlots() {
        for (let key in this.#plotMap) {
            if (this.#plotMap.hasOwnProperty(key)) {
                this.#plotMap[key].forEach(viewer => {
                    viewer.clearHighlights();
                })
            }
        }
    }

    addPlotInstance(plotType, canvas, name, defaultParam) {
        try {
            if (!this.#plotMap[plotType]) {
                this.#plotMap[plotType] = [];
            }

            let isFound = this.#plotMap[plotType].some(plot => plot.name === name);

            if (!isFound) {
                let newChartInstance
                switch(plotType) {
                    case 'bar': 
                        if (defaultParam != undefined) newChartInstance = new BarPlot(defaultParam)
                            else newChartInstance = new BarPlot()
                            break;
                    case 'donut':
                        if (defaultParam != undefined) newChartInstance = new DonutPlot(defaultParam)
                        else newChartInstance = new DonutPlot()
                        break;
                    case 'histogram':
                        if (defaultParam != undefined) newChartInstance = new HistogramPlot(defaultParam)
                            else newChartInstance = new HistogramPlot()
                            break;
                    case 'line':
                        if (defaultParam != undefined) newChartInstance = new LinePlot(defaultParam)
                            else newChartInstance = new LinePlot()
                            break;
                    case 'parallelCoordinate':
                        if (defaultParam != undefined) newChartInstance = new ParallelCoordinatePlot(defaultParam)
                            else newChartInstance = new ParallelCoordinatePlot()
                            break;
                    case 'pie':
                        if (defaultParam != undefined) newChartInstance = new PiePlot(defaultParam)
                            else newChartInstance = new PiePlot()
                            break;
                    case 'scatter':
                        if (defaultParam != undefined) newChartInstance = new ScatterPlot(defaultParam)
                            else newChartInstance = new ScatterPlot()
                            break;
                }
                newChartInstance.addViewer(canvas, name);
                this.#plotMap[plotType].push(newChartInstance);

                return {
                    status: "success",
                    message: "Viewer added"
                };
            } else {
                return {
                    status: "failed",
                    message: "Viewer with given name already exists. Please use different."
                };
            }
        } catch (err) {
            return {
                status: "failed",
                message: "Viewer not added: " + err.message
            };
        }
    }

    getAllPlots(plotType) {
        return this.#plotMap[plotType] || [];
    }

    getPlotByName(plotType, name) {
        return this.#plotMap[plotType].find(plot => plot.name === name);
    }

    getPlotsCount(plotType) {
        return (this.#plotMap[plotType] || []).length;
    }

    clearAllPlots(plotType) {
        if (this.#plotMap[plotType]) {
            this.#plotMap[plotType].forEach(plot => plot.destroy());
            this.#plotMap[plotType] = [];
        }
    }

    clearPlotByName(plotType, name) {
        if (this.#plotMap[plotType]) {
            let index = this.#plotMap[plotType].findIndex(plot => plot.name === name);
            if (index !== -1) {
                this.#plotMap[plotType][index].destroy();
                this.#plotMap[plotType].splice(index, 1);
            }
        }
    }
}

const plotMgrInstance = new PlotMgr();
Object.freeze(plotMgrInstance);
export default plotMgrInstance;
